import { useId, useState, type ReactNode } from "react";
import { Icon } from "./Icon";

/**
 * A short explanation attached to a metric.
 *
 * Every number on this dashboard is a derived score with a specific meaning —
 * NDI is "how far the engines have drifted" and lower is better, MPTR is a
 * percentage of a denominator that is not the whole pillar. A CCO reading
 * "40.7" with no idea which direction is good will either mistrust it or
 * misreport it, and both are worse than the number being absent.
 *
 * Deliberately not a `title` attribute: those don't appear on keyboard focus,
 * don't show on touch, and can't be styled. This shows on hover AND focus, is
 * wired with aria-describedby so a screen reader announces it with the metric,
 * and closes on Escape.
 */
export function InfoTip({
  label,
  children,
  align = "center",
}: {
  /** Names what is being explained, for the accessible label. */
  label: string;
  children: ReactNode;
  /** `end` keeps the bubble on screen for a trigger near the right edge. */
  align?: "center" | "end";
}) {
  const id = useId();
  const [open, setOpen] = useState(false);

  return (
    <span className={`infotip${align === "end" ? " infotip--end" : ""}`}>
      <button
        type="button"
        className="infotip__trigger"
        aria-label={`What is ${label}?`}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
        // The card behind this is a link to the detail view; explaining a
        // metric shouldn't navigate away from it.
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <Icon name="info" size={16} />
      </button>
      {open ? (
        <span className="infotip__bubble" role="tooltip" id={id}>
          {children}
        </span>
      ) : null}
    </span>
  );
}
